import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import './SoloWiseLoadingOverlay.css';
import owlSrc from './assets/solowise-owl-open.webp';

export interface SoloWiseLoadingOverlayProps {
  open: boolean;
  label?: string;
}

const EXIT_MS = 180;

export function SoloWiseLoadingOverlay({
  open,
  label = 'Loading',
}: SoloWiseLoadingOverlayProps) {
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }

    const timer = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className={`solowise-loading-overlay${open ? ' is-visible' : ''}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
    >
      <div className="solowise-loader-stage" aria-hidden="true">
        <svg className="solowise-loader-rings" viewBox="0 0 100 100">
          <circle className="solowise-pulse solowise-pulse--one" cx="50" cy="50" r="44" />
          <circle className="solowise-pulse solowise-pulse--two" cx="50" cy="50" r="44" />
          <circle className="solowise-ring-base" cx="50" cy="50" r="44" />
        </svg>

        <div className="solowise-owl-wrap">
          <img className="solowise-owl" src={owlSrc} alt="" draggable="false" />

          <svg className="solowise-eyelid solowise-eyelid--left" viewBox="0 0 100 100">
            <defs>
              <radialGradient id="solowiseLeftLid" cx="42%" cy="30%" r="78%">
                <stop offset="0" stopColor="#cceaff" />
                <stop offset="0.7" stopColor="#91cbfb" />
                <stop offset="1" stopColor="#79baf1" />
              </radialGradient>
            </defs>
            <ellipse cx="50" cy="50" rx="49" ry="49" fill="url(#solowiseLeftLid)" />
            <path d="M19 49 Q50 68 81 49" fill="none" stroke="#14469b" strokeWidth="8" strokeLinecap="round" />
          </svg>

          <svg className="solowise-eyelid solowise-eyelid--right" viewBox="0 0 100 100">
            <defs>
              <radialGradient id="solowiseRightLid" cx="42%" cy="30%" r="78%">
                <stop offset="0" stopColor="#cceaff" />
                <stop offset="0.7" stopColor="#91cbfb" />
                <stop offset="1" stopColor="#79baf1" />
              </radialGradient>
            </defs>
            <ellipse cx="50" cy="50" rx="49" ry="49" fill="url(#solowiseRightLid)" />
            <path d="M19 49 Q50 68 81 49" fill="none" stroke="#14469b" strokeWidth="8" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>,
    document.body,
  );
}
